/** Main palette of the default brand (current InsuCoach look). Keys name the role, not the hue. */

/** Solid colors (hex) */
export const colors = {
  // Brand gradient (KPMG Blue -> Cobalt)
  brandPrimary: '#00338D',
  brandPrimaryAlt: '#1E49E2',
  // Status
  success: '#0A8754',
  successAlt: '#3DBB7E',
  successVivid: '#3DD598',
  warning: '#D97706',
  warningAccent: '#FFB020',
  danger: '#DC2626',
  dangerLight: '#EF4444',
  successTint: '#E6F4EE',
  warningTint: '#FEF3C7',
  dangerTint: '#FEE2E2',
  // Danger text on a danger tint or wash (pills, badges).
  dangerOnTint: '#DC2626',
  // Text
  textPrimary: '#0C233C',
  textSecondary: '#3E5066',
  textTertiary: '#7B8AA0',
  textMuted: '#8BA3BD',
  textMutedAlt: '#8BA5BC',
  textDeep: '#456583',
  ink: '#0D1626',
  onDark: '#FFFFFF',
  // Borders / surfaces
  border: '#D4DEEB',
  borderStrong: '#B0BFD3',
  borderStrongAlt: '#C9D2EE',
  surface: '#EDF2F9',
  surfaceTint: '#EAF1FA',
  surfaceTintAlt: '#D6E8F2',
  canvas: '#F4F7FB',
  canvasAlt: '#F6F9FD',
  elevated: '#EEF3F9',
  elevatedAlt: '#E4ECF6',
  surfaceCard: '#FFFFFF',
  heroCardEnd: '#0D1626',
  // Mask stops must stay opaque for the mask to show the element.
  maskOpaque: '#000000',
  // Accent ramp used across the landing page and marketing visuals
  accent: '#00B8F5',
  accentMid: '#0E94C9',
  accentDeep: '#0078B5',
  accentDeepHover: '#00527A',
  accentLight: '#3DD5F3',
  accentSoft: '#5BB8F8',
  accentHighlight: '#5BDDF8',
  accentText: '#2E6BE6',
  accentTint: '#E0E5F7',
  // "Unique" (user-created) content markers
  accentUnique: '#7C3AED',
  accentUniqueOnDark: '#C9A6FF',
  coverChipAccent: '#5BDDF8',
  // Rotating tints for traits, stages and skills
  categorical1: '#4F46E5',
  categorical2: '#7C3AED',
  categorical3: '#BE185D',
  categorical4: '#0891B2',
  categorical5: '#2563EB',
  // Admin chips keyed by kind
  chipAccent1: '#4F46E5',
  chipAccent2: '#7213EA',
  levelHigh: '#FF8A4C',
  // Score bars and rings
  scoreGoodEnd: '#3DBB7E',
  scoreFairEnd: '#F4B860',
  scorePoorEnd: '#F0697A',
  dangerButtonStart: '#F0697A',
  progressRingStart: '#3D8BF0',
  progressRingEnd: '#2563EB',
  chartSeries1: '#3DD598',
  chartSeries2: '#3DBB7E',
  chartSeries3: '#3D8BF0',
  chartSeries4: '#D97706',
  chartSeries5: '#00B8F5',
  // Medal / trophy art
  rewardHighlight: '#FEF3C7',
  rewardMid: '#FFC857',
  rewardBase: '#FFB020',
  // Robot mascot illustration
  mascotBodyEnd: '#E3E8FF',
  mascotOutline: '#E0E5F7',
  mascotDetail: '#7E8AEA',
  // Avatar and cover gradients
  avatarGradientEnd1: '#7213EA',
  avatarGradientEnd2: '#FD349C',
  personaCover1Start: '#001E5A',
  personaCover3Start: '#003A9E',
  personaCover3End: '#2E6BE6',
  personaCover4Start: '#00527A',
  personaCover4End: '#0E94C9',
  personaCover5Start: '#0A4DA6',
  personaCover5End: '#3D8BF0',
  personaCover6End: '#5BB8F8',
  scenarioCover3Start: '#7C3AED',
  scenarioCover3End: '#B06AF6',
  scenarioCover4Start: '#0E7C9B',
  scenarioCover4End: '#3DD5F3',
  scenarioCover5End: '#FF9F5C',
  scenarioCover6End: '#5BDDF8',
  // Persona profile category accents
  personaCategoryDemographic: '#00B8F5',
  personaCategoryProfessional: '#FFB020',
  personaCategoryHousehold: '#EF4444',
  personaCategoryFinancial: '#3DBB7E',
  personaCategoryInvestment: '#0E94C9',
  personaCategoryHealth: '#0891B2',
  personaCategoryInsurance: '#00B8F5',
  personaCategoryPurchase: '#FF8A4C',
  // Roleplay policy document: toolbar text on the dark stage, then the white A4 page.
  policyDocToolbarText: '#ECEAFF',
  policyDocText: '#1E293B',
  policyDocTextMuted: '#64748B',
  policyDocTextFaint: '#94A3B8',
  policyDocBorder: '#E2E8F0',
  policyDocDivider: '#F1F5F9',
  policyDocPanel: '#F8FAFC',
  policyDocAccent: '#2563EB',
  policyDocAccentEnd: '#4F46E5',
  policyDocHeadingRule: '#DBEAFE',
  policyDocHighlightRow: '#EFF6FF',
  policyDocPositive: '#16A34A',
} as const;

/** RGB channels for rgba() — use with `brandRgba('brandPrimary', 0.1)` */
export const rgb = {
  brandPrimary: '0,51,141',
  brandPrimaryAlt: '30,73,226',
  success: '10,135,84',
  successVivid: '61,213,152',
  warning: '217,119,6',
  rewardBase: '255,176,32',
  danger: '220,38,38',
  dangerLight: '239,68,68',
  dangerOnTint: '220,38,38',
  categorical1: '79,70,229',
  categorical2: '124,58,237',
  categorical3: '190,24,93',
  categorical4: '8,145,178',
  categorical5: '37,99,235',
  chipAccent1: '79,70,229',
  chipAccent2: '114,19,234',
  accent: '0,184,245',
  accentMid: '14,148,201',
  accentDeep: '0,120,181',
  accentLight: '61,213,243',
  accentSoft: '91,184,248',
  accentHighlight: '91,221,248',
  accentTint: '224,229,247',
  accentUniqueOnDark: '201,166,255',
  coverChipAccent: '91,221,248',
  surfaceTint: '234,241,250',
  textPrimary: '12,35,60',
  surfaceCard: '255,255,255',
  onDark: '255,255,255',
  // Channels below have no solid of the same name.
  accentTintAlt: '227,232,255',
  accentUniqueTint: '114,19,234',
  glowWarm: '254,226,226',
  dangerWash: '253,52,156',
  // Soft danger border tint on ghost danger buttons; the design literal is rgba(255,107,138,0.4).
  dangerSoft: '255,107,138',
  // Dark neutrals for shadows, scrims and photo overlays.
  scrim: '7,23,43',
  scrimSoft: '10,37,64',
  pickerScrim: '17,24,39',
  shadow: '0,0,0',
  shadowDeep: '10,37,64',
  toggleShadow: '17,24,39',
  imageOverlay: '12,22,38',
  // Roleplay policy document chrome (dark stage).
  policyDocBackdrop: '8,6,20',
  policyDocStage: '10,8,24',
  policyDocToolbarFrom: '28,22,52',
  policyDocToolbarTo: '18,14,38',
  policyDocToolbarBorder: '123,95,255',
} as const;

/** Gradient presets shared across the default brand pages (KPMG Blue -> Cobalt). */
export const gradients = {
  brandPrimary: `linear-gradient(135deg,${colors.brandPrimary} 0%,${colors.brandPrimaryAlt} 100%)`,
} as const;
